/**
 * The Hubs shell (07-hubs §6; layout per Jon 2026-11-03): a dark top bar (search,
 * client switcher, Ask Lupa, user menu), the left rail with fly-outs (HubRail),
 * a breadcrumb strip at the top of the page, then the page on the light
 * background. Phones get a hamburger and the drawer (HubDrawer) instead of the
 * rail. Every DWA page inside the Hubs renders it via <HubLayout> /
 * <FullHeightHubLayout>; the entryway (/) and client-role users don't.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { shouldShowLupaWidget } from '../routes/routeRules';
import { canSeeItem, clientSiblings, resolveHubLocation } from './hubs';
import { openLupaWidget, readRailCollapsed, writeRailCollapsed } from './hubNav';
import { rememberClient, usePinnedItems } from './preferences';
import { GlobalSearch } from './GlobalSearch';
import { HubDrawer } from './HubDrawer';
import { HubRail } from './HubRail';
import { UserMenu } from './UserMenu';

export const TOP_BAR_HEIGHT = 56;

const selectStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: DWA.onDark,
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: DWA.radius,
  padding: '0.35rem 0.5rem',
  fontSize: '0.8rem',
  fontFamily: DWA.font,
  maxWidth: '11rem',
  minWidth: 0,
  cursor: 'pointer',
};

const iconButton: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  border: 'none',
  background: 'transparent',
  color: DWA.onDark,
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/** Client switcher for client-specific items (07-hubs §2 rule 5). */
const ClientSwitcher: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { item } = resolveHubLocation(pathname, search);
  const clients = item ? clientSiblings(item).filter((i) => canSeeItem(user, i)) : [];

  // Remember the client last viewed, per user.
  useEffect(() => {
    if (user?.id && item?.clientId) rememberClient(user.id, item.clientId);
  }, [user?.id, item?.clientId]);

  if (!item || clients.length < 2) return null;
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 1, minWidth: 0 }}>
      <span className="ow-hide-mobile" style={{ fontSize: '0.75rem', color: DWA.onDarkMuted }}>
        Client
      </span>
      <select
        aria-label="Switch client"
        value={item.id}
        onChange={(e) => {
          const next = clients.find((c) => c.id === e.target.value);
          if (next?.route) navigate(next.route);
        }}
        style={selectStyle}
      >
        {clients.map((c) => (
          <option key={c.id} value={c.id} style={{ color: DWA.ink }}>
            {c.clientLabel ?? c.title}
          </option>
        ))}
      </select>
    </label>
  );
};

export const HubTopBar: React.FC<{
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  hamburgerRef: React.RefObject<HTMLButtonElement>;
  sticky: boolean;
}> = ({ drawerOpen, onOpenDrawer, hamburgerRef, sticky }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const lupaAvailable = shouldShowLupaWidget(pathname, user);

  return (
    <header
      data-testid="hub-top-bar"
      style={{
        background: DWA.darkBar,
        borderBottom: `1px solid ${DWA.railLine}`,
        color: DWA.onDark,
        fontFamily: DWA.font,
        padding: '0 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        height: TOP_BAR_HEIGHT,
        flexShrink: 0,
        zIndex: 150,
        ...(sticky ? { position: 'sticky', top: 0 } : { position: 'relative' }),
      }}
    >
      <button
        ref={hamburgerRef}
        type="button"
        className="ow-only-mobile ow-icon-button"
        onClick={onOpenDrawer}
        aria-label="Open navigation"
        aria-expanded={drawerOpen}
        aria-haspopup="dialog"
        style={iconButton}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>

      <Link to="/" aria-label="All Hubs" title="All Hubs" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', textDecoration: 'none', flexShrink: 0 }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${DWA.dark}, ${DWA.darkSoft})`,
            border: '1px solid rgba(254,195,92,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={BrandIcon} alt="" style={{ width: 22, height: 22, objectFit: 'cover' }} />
        </span>
        <span className="ow-hide-mobile" style={{ color: DWA.onDark, fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
          Omega Wolf
        </span>
      </Link>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', padding: '0 0.25rem' }}>
        <GlobalSearch />
      </div>

      <ClientSwitcher />

      {lupaAvailable && (
        <button
          type="button"
          onClick={() => openLupaWidget()}
          aria-label="Ask Lupa"
          title="Ask Lupa"
          className="ow-ask-lupa"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            height: 34,
            padding: '0 0.7rem 0 0.35rem',
            borderRadius: 999,
            border: '1px solid rgba(254,195,92,0.45)',
            background: 'rgba(254,195,92,0.08)',
            color: DWA.gold,
            fontWeight: 700,
            fontSize: '0.8rem',
            fontFamily: DWA.font,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <img src={BrandIcon} alt="" style={{ width: 24, height: 24, objectFit: 'cover' }} />
          <span className="ow-hide-mobile">Ask Lupa</span>
        </button>
      )}

      <UserMenu />
    </header>
  );
};

const crumbSep = <span aria-hidden="true" style={{ color: DWA.textMuted }}>›</span>;

/** Breadcrumb (Hubs › <home Hub> › <item>) at the top of the page, with a pin toggle on items. */
export const PageCrumbs: React.FC = () => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const { hub, item } = resolveHubLocation(pathname, search);
  const { isPinned, togglePin } = usePinnedItems(user?.id);
  if (!hub) return null;
  const pinned = item ? isPinned(item.id) : false;

  return (
    <div
      data-testid="page-crumbs"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.5rem clamp(1rem, 3vw, 1.75rem)',
        background: DWA.dark,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        fontFamily: DWA.font,
        minHeight: 40,
      }}
    >
      <nav aria-label="Breadcrumb" style={{ minWidth: 0, flex: 1 }}>
        <ol style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.45rem', margin: 0, padding: 0, fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <li>
            <Link to="/" style={{ color: DWA.onDarkMuted, textDecoration: 'none' }}>
              Hubs
            </Link>
          </li>
          <li>{crumbSep}</li>
          <li style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item ? (
              <Link to={hub.route} style={{ color: DWA.onDarkMuted, textDecoration: 'none' }}>
                {hub.title}
              </Link>
            ) : (
              <span aria-current="page" style={{ color: '#ffffff', fontWeight: 700 }}>
                {hub.title}
              </span>
            )}
          </li>
          {item && (
            <>
              <li>{crumbSep}</li>
              <li aria-current="page" style={{ color: '#ffffff', fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.title}
              </li>
            </>
          )}
        </ol>
      </nav>
      {item?.route && (
        <button
          type="button"
          onClick={() => togglePin(item.id)}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            border: `1px solid ${pinned ? DWA.gold : 'rgba(255,255,255,0.25)'}`,
            background: pinned ? DWA.goldSoft : 'transparent',
            color: pinned ? DWA.gold : DWA.onDarkMuted,
            borderRadius: 999,
            padding: '0.2rem 0.65rem',
            fontSize: '0.75rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: DWA.font,
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faThumbtack} aria-hidden="true" style={{ fontSize: '0.7rem' }} />
          {pinned ? 'Pinned' : 'Pin'}
        </button>
      )}
    </div>
  );
};

/** Shell state + frame shared by both layouts. */
const HubShell: React.FC<{ fullHeight: boolean; children: React.ReactNode }> = ({ fullHeight, children }) => {
  const [collapsedPref, setCollapsedPref] = useState(readRailCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const toggleCollapsed = () => {
    const next = !collapsedPref;
    writeRailCollapsed(next);
    setCollapsedPref(next);
  };
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Give focus back to the hamburger when the drawer closes.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !drawerOpen) hamburgerRef.current?.focus();
    wasOpen.current = drawerOpen;
  }, [drawerOpen]);

  return (
    <div
      className="ow-shell"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: DWA.pageBg,
        ...(fullHeight ? { height: '100dvh', overflow: 'hidden' } : { minHeight: '100vh' }),
      }}
    >
      <HubTopBar drawerOpen={drawerOpen} onOpenDrawer={() => setDrawerOpen(true)} hamburgerRef={hamburgerRef} sticky={!fullHeight} />
      {/* Rail-coloured row: the rail is a fixed-height sticky panel, so on a page that
          scrolls the strip below it would otherwise show the light shell background. */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, background: DWA.rail }}>
        {/* The Lupa Hub keeps the rail narrow: the chat has its own conversation sidebar. */}
        <HubRail
          collapsed={fullHeight ? true : collapsedPref}
          onToggleCollapsed={fullHeight ? undefined : toggleCollapsed}
          sticky={!fullHeight}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: DWA.pageBg, ...(fullHeight ? { minHeight: 0, position: 'relative' } : {}) }}>
          {!fullHeight && <PageCrumbs />}
          {fullHeight ? <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>{children}</div> : children}
        </div>
      </div>
      <HubDrawer open={drawerOpen} onClose={closeDrawer} />
    </div>
  );
};

/** Page frame for every DWA page inside the Hubs (scrolls with the window). */
export const HubLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => <HubShell fullHeight={false}>{children}</HubShell>;

/** Full-height frame for the Lupa Hub: the chat fills the rest of the viewport. */
export const FullHeightHubLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => <HubShell fullHeight>{children}</HubShell>;
